import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
    UserPlus, Shield, User as UserIcon, KeyRound, Power, PowerOff, X, Loader2, Unlock,
} from "lucide-react";

const ROLE_LABEL = { admin: "Admin", employee: "Employee" };
const ROLE_STYLES = {
    admin:    "bg-[#0E2A47] text-white",
    employee: "bg-[#EFEBE0] text-[#1A2230]",
};

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const { user: me } = useAuth();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const toggleActive = async (u) => {
        try {
            const { data } = await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            setUsers((all) => all.map((x) => (x.id === u.id ? data : x)));
            toast.success(`${data.name} ${data.is_active ? "activated" : "deactivated"}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const clearLockout = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.id}/clear-lockout`);
            if (data.cleared === 0) {
                toast.info(`${u.name} has no active lockout — they can log in now.`);
            } else {
                toast.success(`Cleared ${data.cleared} lockout record${data.cleared === 1 ? "" : "s"} for ${u.name}.`);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Admin
                    </p>
                    <h1 className="mt-2 font-[Outfit] text-3xl font-semibold tracking-tight">
                        Users
                    </h1>
                    <p className="mt-2 text-[14px] text-[#6F6B63] max-w-2xl">
                        Invite staff, promote to admin, or deactivate departed
                        employees. All user actions are recorded in the audit
                        trail.
                    </p>
                </div>
                <button
                    onClick={() => setShowInvite(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0E2A47] text-white px-5 py-2.5 text-[14px] hover:bg-[#08182B] transition-colors"
                    data-testid="invite-user-btn"
                >
                    <UserPlus className="h-4 w-4" /> Invite user
                </button>
            </div>

            {error && (
                <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden">
                {loading ? (
                    <p className="p-6 text-[14px] text-[#6F6B63]">Loading users…</p>
                ) : users.length === 0 ? (
                    <p className="p-6 text-[14px] text-[#6F6B63]">No users yet.</p>
                ) : (
                    <table className="w-full text-[13.5px]">
                        <thead className="bg-[#F7F5F0] text-[#6F6B63] text-[11.5px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-5 py-3 font-medium">Name / email</th>
                                <th className="text-left px-5 py-3 font-medium">Role</th>
                                <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Phone</th>
                                <th className="text-left px-5 py-3 font-medium hidden lg:table-cell">Last login</th>
                                <th className="text-right px-5 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <UserRow
                                    key={u.id}
                                    u={u}
                                    isSelf={u.id === me?.id}
                                    onToggleActive={toggleActive}
                                    onResetPassword={() => setResetTarget(u)}
                                    onClearLockout={clearLockout}
                                    onRoleChanged={load}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showInvite && (
                <InviteUserModal
                    onClose={() => setShowInvite(false)}
                    onCreated={(u) => { setUsers((all) => [u, ...all]); setShowInvite(false); }}
                />
            )}
            {resetTarget && (
                <ResetPasswordModal
                    target={resetTarget}
                    onClose={() => setResetTarget(null)}
                    onDone={() => setResetTarget(null)}
                />
            )}
        </div>
    );
}

function UserRow({ u, isSelf, onToggleActive, onResetPassword, onClearLockout, onRoleChanged }) {
    const [saving, setSaving] = useState(false);
    const promote = async () => {
        if (u.role === "admin") return;
        setSaving(true);
        try {
            await api.patch(`/users/${u.id}`, { role: "admin" });
            toast.success(`${u.name} promoted to admin`);
            onRoleChanged();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };
    return (
        <tr className="border-t border-[#E5DFD3] hover:bg-[#FAF8F2]" data-testid={`user-row-${u.email}`}>
            <td className="px-5 py-3.5">
                <div className="flex items-center gap-3">
                    <span className="h-8 w-8 rounded-full bg-[#EFEBE0] text-[#0E2A47] grid place-items-center font-semibold text-[12px]">
                        {u.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                        <p className="font-medium">{u.name}{isSelf && " (you)"}</p>
                        <p className="text-[11.5px] text-[#6F6B63] truncate">{u.email}</p>
                    </div>
                </div>
            </td>
            <td className="px-5 py-3.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_STYLES[u.role]}`}>
                    {u.role === "admin" ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                    {ROLE_LABEL[u.role]}
                </span>
                {u.must_change_password && (
                    <span className="ml-2 text-[10.5px] text-[#8a6a1f] italic">must change pw</span>
                )}
            </td>
            <td className="px-5 py-3.5 text-[#6F6B63] hidden md:table-cell">{u.phone || "—"}</td>
            <td className="px-5 py-3.5 text-[#6F6B63] hidden lg:table-cell">
                {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}
            </td>
            <td className="px-5 py-3.5 text-right">
                <div className="inline-flex items-center gap-1">
                    {u.role === "employee" && (
                        <button
                            onClick={promote}
                            disabled={saving}
                            className="p-1.5 rounded-full hover:bg-[#EFEBE0] text-[#0E2A47]"
                            title="Promote to admin"
                            data-testid={`promote-${u.email}`}
                        >
                            <Shield className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button
                        onClick={onResetPassword}
                        className="p-1.5 rounded-full hover:bg-[#EFEBE0] text-[#0E2A47]"
                        title="Reset password"
                        data-testid={`reset-pw-${u.email}`}
                    >
                        <KeyRound className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={() => onClearLockout(u)}
                        className="p-1.5 rounded-full hover:bg-[#EFEBE0] text-[#0E2A47]"
                        title="Clear brute-force lockout"
                        data-testid={`clear-lockout-${u.email}`}
                    >
                        <Unlock className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={() => onToggleActive(u)}
                        disabled={isSelf}
                        className="p-1.5 rounded-full hover:bg-[#EFEBE0] disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.is_active ? "Deactivate" : "Reactivate"}
                        data-testid={`toggle-${u.email}`}
                    >
                        {u.is_active
                            ? <PowerOff className="h-3.5 w-3.5 text-[#a04545]" />
                            : <Power className="h-3.5 w-3.5 text-[#3d6442]" />}
                    </button>
                </div>
            </td>
        </tr>
    );
}

function InviteUserModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [role, setRole] = useState("employee");
    const [pw, setPw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!name || !email || pw.length < 8) {
            setError("Name, email, and an 8+ character temporary password are required.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/users", {
                name, email, phone: phone || null, role, temp_password: pw,
            });
            toast.success(`Invited ${data.name}. Share their temporary password securely.`);
            onCreated(data);
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-[#1A2230]/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl border border-[#E5DFD3] p-6 space-y-3"
                data-testid="invite-user-modal"
            >
                <div className="flex items-start justify-between">
                    <h3 className="font-[Outfit] text-xl font-semibold">Invite a user</h3>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#EFEBE0]" data-testid="invite-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name *" className="rtc-input" data-testid="invite-name" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email *" className="rtc-input" data-testid="invite-email" />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="rtc-input" />
                <select value={role} onChange={(e) => setRole(e.target.value)} className="rtc-input" data-testid="invite-role">
                    <option value="employee">Employee</option>
                    <option value="admin">Admin (Valerie / Dorothy only)</option>
                </select>
                <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Temporary password (they'll change on first login) *" className="rtc-input" data-testid="invite-temp-pw" />
                {error && <p className="text-[13px] text-[#a04545]" data-testid="invite-error">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full rounded-full bg-[#0E2A47] text-white py-2.5 text-[14px] font-medium hover:bg-[#08182B] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="invite-submit">
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send invite
                </button>
                <p className="text-[11px] text-[#8F8B83] text-center">
                    They will be forced to change this password when they first log in.
                </p>
            </form>
        </div>
    );
}

function ResetPasswordModal({ target, onClose, onDone }) {
    const [pw, setPw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return setError("Must be 8+ characters.");
        setSubmitting(true);
        try {
            await api.post(`/users/${target.id}/reset-password`, { new_temp_password: pw });
            toast.success(`Password reset for ${target.name}. Share the new temp securely.`);
            onDone();
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-[#1A2230]/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl border border-[#E5DFD3] p-6 space-y-3"
                data-testid="reset-pw-modal"
            >
                <h3 className="font-[Outfit] text-xl font-semibold">Reset password</h3>
                <p className="text-[13.5px] text-[#6F6B63]">
                    Setting a new temporary password for <strong>{target.name}</strong>. They will be forced to change it on next login.
                </p>
                <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New temporary password *" className="rtc-input" data-testid="reset-pw-input" />
                {error && <p className="text-[13px] text-[#a04545]">{error}</p>}
                <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="flex-1 rounded-full border border-[#E5DFD3] py-2.5 text-[14px] hover:border-[#0E2A47]">Cancel</button>
                    <button type="submit" disabled={submitting} className="flex-1 rounded-full bg-[#0E2A47] text-white py-2.5 text-[14px] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="reset-pw-submit">
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
}
